import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { ArrowLeft, Sparkles, Download, RefreshCw, Trash2, Film, Mic } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import MediaPicker from "@/components/MediaPicker";

const videoUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/video/${id}/file?auth=${encodeURIComponent(localStorage.getItem("bt_token") || "")}`;

function Wizard({ onDone }) {
  const { t, lang } = useLang();
  const { user, setUser, refresh } = useAuth();
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("cat");
  const [category, setCategory] = useState("product");
  const [tone, setTone] = useState("auto");
  const [media, setMedia] = useState([]);
  const [form, setForm] = useState({ product_name: "", description: "", price: "", offer: "", cta: "", link: "" });
  const [voice, setVoice] = useState(false);
  const [duration, setDuration] = useState(20);
  const [music, setMusic] = useState("dynamic");
  const [job, setJob] = useState(null);
  const [busy, setBusy] = useState(false);
  const [storyboard, setStoryboard] = useState(null);
  const [sbBusy, setSbBusy] = useState(false);
  const timerRef = React.useRef(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => { api.get("/video/config").then((r) => setConfig(r.data)); }, []);
  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const poll = (id) => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/video/${id}`);
        setJob(data);
        if (data.status === "done") { clearInterval(timerRef.current); confetti({ particleCount: 140, spread: 80, origin: { y: 0.3 }, colors: ["#7C3AED", "#2563EB"] }); refresh(); }
        if (data.status === "failed") { clearInterval(timerRef.current); toast.error(t("err_generic")); }
      } catch (e) {}
    }, 4000);
  };

  const generate = async () => {
    if (!form.product_name.trim()) { toast.error(t("err_generic")); return; }
    setBusy(true); setStep("result");
    try {
      const { data } = await api.post("/video", { category, tone, ...form, media_ids: media, with_voiceover: voice, duration, music_style: music, storyboard });
      if (user) setUser({ ...user, credits: data.credits });
      setJob({ id: data.id, status: "queued" });
      poll(data.id);
    } catch (e) {
      if (e.response?.status === 402) toast.error(t("err_credits")); else toast.error(t("err_generic"));
      setStep("storyboard");
    } finally { setBusy(false); }
  };

  const genStoryboard = async () => {
    if (!form.product_name.trim()) { toast.error(t("err_generic")); return; }
    setSbBusy(true); setStep("storyboard");
    try {
      const { data } = await api.post("/video/storyboard", { category, tone, product_name: form.product_name, description: form.description, price: form.price, offer: form.offer, cta: form.cta, duration });
      setStoryboard(data);
    } catch (e) {
      toast.error(t("err_generic")); setStep("info");
    } finally { setSbBusy(false); }
  };

  const updateScene = (i, key, value) => {
    setStoryboard((sb) => {
      const scenes = sb.scenes.map((s, idx) => idx === i ? { ...s, [key]: value } : s);
      return { ...sb, scenes };
    });
  };

  const reset = () => { setStep("cat"); setCategory("product"); setTone("auto"); setMedia([]); setForm({ product_name: "", description: "", price: "", offer: "", cta: "", link: "" }); setVoice(false); setStoryboard(null); setJob(null); onDone && onDone(); };
  const Back = ({ to }) => <button onClick={to} data-testid="video-back" className="inline-flex items-center gap-2 text-stone-500 font-semibold mb-5 hover:text-stone-800"><ArrowLeft size={20} /> {t("back")}</button>;

  if (!config) return <div className="text-center py-20 text-stone-400 animate-pulse">{t("loading")}</div>;

  return (
    <AnimatePresence mode="wait">
      {step === "cat" && (
        <motion.div key="cat" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <h2 className="font-head font-black text-3xl text-center mb-8">{t("video_q_cat")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {config.categories.map((c) => (
              <button key={c.id} onClick={() => { setCategory(c.id); setStep("media"); }} data-testid={`vcat-${c.id}`}
                className={`text-left p-5 rounded-3xl border-2 bg-white hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow] ${category === c.id ? "border-primary" : "border-stone-200"}`}>
                <div className="text-3xl mb-2">{c.emoji}</div>
                <div className="font-head font-bold text-lg">{lang === "fr" ? c.fr : c.en}</div>
              </button>
            ))}
          </div>
        </motion.div>
      )}

      {step === "media" && (
        <motion.div key="media" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <Back to={() => setStep("cat")} />
          <h2 className="font-head font-black text-3xl text-center mb-2">📸 {t("video_q_media")}</h2>
          <p className="text-center text-stone-500 mb-6">{t("video_media_sub")}</p>
          <div className="max-w-2xl mx-auto">
            <MediaPicker selectable selected={media} onChange={setMedia} />
            <Button onClick={() => setStep("info")} disabled={media.length === 0} data-testid="video-media-continue"
              className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[56px] font-bold text-lg mt-6">
              {t("media_continue")} ({media.length})
            </Button>
          </div>
        </motion.div>
      )}

      {step === "info" && (
        <motion.div key="info" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <Back to={() => setStep("media")} />
          <h2 className="font-head font-black text-3xl text-center mb-8">{t("video_q_info")}</h2>
          <div className="max-w-xl mx-auto space-y-4">
            <div><Label className="font-semibold">{t("vf_product")}</Label><Input value={form.product_name} onChange={set("product_name")} data-testid="vf-product" className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1" /></div>
            <div><Label className="font-semibold">{t("vf_desc")}</Label><Textarea value={form.description} onChange={set("description")} data-testid="vf-desc" className="rounded-xl border-2 border-stone-300 mt-1" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="font-semibold">{t("vf_price")}</Label><Input value={form.price} onChange={set("price")} className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1" /></div>
              <div><Label className="font-semibold">{t("vf_offer")}</Label><Input value={form.offer} onChange={set("offer")} className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1" /></div>
            </div>
            <div><Label className="font-semibold">{t("vf_cta")}</Label><Input value={form.cta} onChange={set("cta")} data-testid="vf-cta" className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1" /></div>
            <div><Label className="font-semibold">{t("vf_link")}</Label><Input value={form.link} onChange={set("link")} className="min-h-[52px] rounded-xl border-2 border-stone-300 mt-1" /></div>
            <div>
              <Label className="font-semibold">{t("vf_tone")}</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {config.tones.map((tn) => (
                  <button key={tn} onClick={() => setTone(tn)} data-testid={`vtone-${tn}`}
                    className={`px-4 py-2 rounded-full text-sm font-semibold border-2 ${tone === tn ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600"}`}>
                    {t(`tone_${tn}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="font-semibold">Durée</Label>
                <div className="flex gap-2 mt-2">
                  {[15, 20, 30].map((d) => (
                    <button key={d} onClick={() => setDuration(d)} data-testid={`vdur-${d}`}
                      className={`flex-1 py-2 rounded-full text-sm font-semibold border-2 ${duration === d ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600"}`}>{d}s</button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="font-semibold">Musique</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {["dynamic", "premium", "viral", "corporate", "elegant", "emotional"].map((m) => (
                    <button key={m} onClick={() => setMusic(m)} data-testid={`vmusic-${m}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border-2 ${music === m ? "border-primary bg-primary/10 text-primary" : "border-stone-200 text-stone-600"}`}>{m}</button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-2xl border-2 border-stone-200 p-4">
              <span className="flex items-center gap-2 font-semibold"><Mic size={18} className="text-primary" /> {t("vf_voice")}</span>
              <Switch checked={voice} onCheckedChange={setVoice} data-testid="vf-voice" />
            </div>
            <p className="text-sm text-stone-400 text-center">💎 {t("video_cost_note")}</p>
            <Button onClick={genStoryboard} disabled={sbBusy || !form.product_name.trim()} data-testid="video-generate"
              className="w-full rounded-full bt-gradient text-white hover:opacity-90 min-h-[60px] font-bold text-lg">
              <Sparkles size={20} className="mr-2" /> {t("sb_next")}
            </Button>
          </div>
        </motion.div>
      )}

      {step === "storyboard" && (
        <motion.div key="storyboard" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
          <Back to={() => setStep("info")} />
          {sbBusy || !storyboard ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6 animate-bounce">📝</div>
              <p className="font-head font-bold text-2xl text-stone-700">{t("sb_generating")}</p>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <h2 className="font-head font-black text-3xl text-center mb-2">🎬 {t("sb_title")}</h2>
              <p className="text-center text-stone-500 mb-6">{t("sb_sub")}</p>
              <div className="space-y-4" data-testid="storyboard-scenes">
                {storyboard.scenes.map((s, i) => (
                  <div key={i} data-testid={`sb-scene-${i}`} className="rounded-3xl border-2 border-stone-200 bg-white p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="grid place-items-center w-8 h-8 rounded-full bt-gradient text-white font-bold text-sm">{i + 1}</span>
                      <span className="font-head font-bold text-lg">{t(`sb_type_${s.type}`) || s.type}</span>
                      {s.duration ? <span className="ml-auto text-xs text-stone-400 font-semibold">{s.duration}s</span> : null}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs font-semibold text-stone-500">{t("sb_heading")}</Label>
                        <Input value={s.heading || ""} onChange={(e) => updateScene(i, "heading", e.target.value)} data-testid={`sb-heading-${i}`} className="min-h-[48px] rounded-xl border-2 border-stone-300 mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold text-stone-500">{t("sb_subtext")}</Label>
                        <Input value={s.subtext || ""} onChange={(e) => updateScene(i, "subtext", e.target.value)} data-testid={`sb-subtext-${i}`} className="min-h-[48px] rounded-xl border-2 border-stone-300 mt-1" />
                      </div>
                      {s.type === "benefits" && (
                        <div>
                          <Label className="text-xs font-semibold text-stone-500">{t("sb_bullets")}</Label>
                          <Textarea value={(s.bullets || []).join("\n")} onChange={(e) => updateScene(i, "bullets", e.target.value.split("\n").map((x) => x.trim()).filter(Boolean))} data-testid={`sb-bullets-${i}`} className="rounded-xl border-2 border-stone-300 mt-1" rows={3} />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {voice && (
                  <div className="rounded-3xl border-2 border-stone-200 bg-white p-5">
                    <Label className="text-xs font-semibold text-stone-500 flex items-center gap-1"><Mic size={14} /> {t("sb_voiceover")}</Label>
                    <Textarea value={storyboard.voiceover || ""} onChange={(e) => setStoryboard((sb) => ({ ...sb, voiceover: e.target.value }))} data-testid="sb-voiceover" className="rounded-xl border-2 border-stone-300 mt-1" rows={3} />
                  </div>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button variant="outline" onClick={genStoryboard} disabled={sbBusy} data-testid="sb-regen" className="rounded-full border-2 min-h-[56px] font-bold sm:flex-1">
                  <RefreshCw size={18} className="mr-2" /> {t("sb_regen")}
                </Button>
                <Button onClick={generate} disabled={busy} data-testid="sb-confirm" className="rounded-full bt-gradient text-white hover:opacity-90 min-h-[56px] font-bold sm:flex-1">
                  <Sparkles size={18} className="mr-2" /> {t("sb_confirm")}
                </Button>
              </div>
              <p className="text-sm text-stone-400 text-center mt-3">💎 {t("video_cost_note")}</p>
            </div>
          )}
        </motion.div>
      )}

      {step === "result" && (
        <motion.div key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          {!job || (job.status !== "done" && job.status !== "failed") ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6 animate-bounce">🎬</div>
              <p className="font-head font-bold text-2xl text-stone-700 mb-2">{t("video_making")}</p>
              <p className="text-primary font-semibold">{job ? t(`video_status_${job.status}`) : ""}</p>
            </div>
          ) : job.status === "failed" ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6">😕</div>
              <p className="font-head font-bold text-2xl text-stone-700 mb-6">{t("err_generic")}</p>
              <Button onClick={() => setStep("storyboard")} data-testid="video-retry" className="rounded-full bg-primary hover:bg-[#6D28D9] min-h-[54px] px-8 font-bold"><RefreshCw size={18} className="mr-2" /> {t("video_new")}</Button>
            </div>
          ) : (
            <div className="max-w-md mx-auto text-center">
              <h2 className="font-head font-black text-3xl mb-6">{t("video_ready")}</h2>
              <video src={videoUrl(job.id)} controls playsInline data-testid="result-video"
                className="w-full rounded-3xl border-4 border-white shadow-xl bg-black aspect-[9/16] object-contain" />
              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <a href={videoUrl(job.id)} download={`${job.title || "betool"}.mp4`} className="flex-1">
                  <Button data-testid="video-download" className="w-full rounded-full bg-primary hover:bg-[#6D28D9] min-h-[54px] font-bold"><Download size={18} className="mr-2" /> {t("video_download")}</Button>
                </a>
                <Button variant="outline" onClick={reset} data-testid="video-new" className="rounded-full border-2 min-h-[54px] font-bold"><RefreshCw size={18} className="mr-2" /> {t("video_new")}</Button>
              </div>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function MyVideos({ refreshKey }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => { try { const { data } = await api.get("/videos"); setItems(data.videos || []); } finally { setLoading(false); } };
  useEffect(() => { load(); }, [refreshKey]);
  const del = async (id) => { await api.delete(`/video/${id}`); setItems(items.filter((i) => i.id !== id)); };

  if (loading) return <div className="text-center py-16 text-stone-400 animate-pulse">{t("loading")}</div>;
  if (items.length === 0) return <div className="bg-white rounded-3xl border-2 border-stone-200 p-12 text-center"><div className="text-5xl mb-4">🎬</div><p className="text-stone-600 text-lg">{t("video_empty")}</p></div>;
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="videos-grid">
      {items.map((v) => (
        <div key={v.id} className="bg-white rounded-3xl border-2 border-stone-200 overflow-hidden" data-testid={`video-item-${v.id}`}>
          {v.status === "done" ? (
            <video src={videoUrl(v.id)} controls playsInline className="w-full aspect-[9/16] object-contain bg-black" />
          ) : (
            <div className="w-full aspect-[9/16] bt-gradient grid place-items-center text-white text-center p-4">
              <div><Film size={40} className="mx-auto mb-2" /><div className="font-semibold">{t(`video_status_${v.status}`)}…</div></div>
            </div>
          )}
          <div className="p-4 flex items-center justify-between gap-2">
            <div className="min-w-0"><div className="font-head font-bold truncate">{v.title || v.product_name}</div><div className="text-xs text-stone-400 uppercase">{v.category}</div></div>
            <Button size="sm" variant="ghost" onClick={() => del(v.id)} data-testid={`video-delete-${v.id}`} className="rounded-full text-red-500 shrink-0"><Trash2 size={16} /></Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CreateVideo() {
  const { t } = useLang();
  const [tab, setTab] = useState("create");
  const [key, setKey] = useState(0);
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-10 min-h-[70vh]">
      <h1 className="font-head font-black text-3xl sm:text-4xl mb-6">🎬 {t("video_title")}</h1>
      <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "mine") setKey((k) => k + 1); }}>
        <TabsList className="rounded-full bg-stone-100 p-1 mb-6">
          <TabsTrigger value="create" data-testid="video-tab-create" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-primary font-semibold px-5">{t("video_tab_create")}</TabsTrigger>
          <TabsTrigger value="mine" data-testid="video-tab-mine" className="rounded-full data-[state=active]:bg-white data-[state=active]:text-primary font-semibold px-5">{t("video_tab_mine")}</TabsTrigger>
        </TabsList>
        <TabsContent value="create"><Wizard onDone={() => { setTab("mine"); setKey((k) => k + 1); }} /></TabsContent>
        <TabsContent value="mine"><MyVideos refreshKey={key} /></TabsContent>
      </Tabs>
    </div>
  );
}
